import React from 'react';
import { FieldCompatibleProps } from '../Field';
import { HelpText } from '../HelpText';
import { InternalCheckbox } from '~/internal/InternalCheckbox';
import { useFullTheme } from '~/design';
import { useId } from '~/utils';

export interface CheckboxProps extends FieldCompatibleProps<boolean> {
  /** The aria-label of the checkbox. */
  'aria-label'?: string;

  /** The aria-labelledby of the checkbox. */
  'aria-labelledby'?: string;

  /** If true, the checkbox cannot be interacted with */
  disabled?: boolean;

  /** If true, the checkbox button appears indeterminate */
  indeterminate?: boolean;

  /** Invalid input if true */
  invalid?: boolean;

  /** Where will the label be placed in relation to the checkbox. (default: `right`). */
  labelPlacement?: 'left' | 'right';

  /** The value of the input control */
  value: boolean;
}

/**
 * Checkboxes allow the user to select one or more items from a set.
 * - If you have a single option, avoid using a checkbox and use the Radio component instead.
 * - The `name` prop is not supported by design to encourage controlled components.
 * - The `checked` prop is not supported to be compatible with our form handling abstraction.
 *
 * ## Accessibility ♿
 * - One of `label`, `aria-label` or `aria-labelledby` _must_ be provided to be fully accessible.
 * - Accessibility features:
 *   - Clicking the label will also activate the checkbox
 *   - Visible focus ring when checkbox has keyboard focus
 *   - Can activate checkbox using space
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    disabled = false,
    helpText,
    indeterminate = false,
    invalid,
    label,
    labelPlacement = 'right',
    onBlur,
    onChange,
    value = false,
  },
  ref,
) {
  const theme = useFullTheme();
  const id = useId();
  const helpTextId = `${id}-helptext`;

  if (!label && !ariaLabel && !ariaLabelledBy) {
    throw new Error(
      'One of `label`, `aria-label`, or `aria-labelledby` is required for accessibility reasons.',
    );
  }
  if ([label, ariaLabel, ariaLabelledBy].filter(Boolean).length > 1) {
    throw new Error('Only one of `label`, `aria-label`, `aria-labelledby` can be used.');
  }

  return (
    // This is fine to disable because we're reacting to blurs from child interactive elements
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[2],
      }}
      onBlur={onBlur}
    >
      <InternalCheckbox
        aria-describedby={helpText ? helpTextId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        checked={value}
        disabled={disabled}
        indeterminate={indeterminate}
        label={label}
        labelPlacement={labelPlacement}
        onChange={event => onChange(event.target.checked)}
        ref={ref}
      />
      {helpText && (
        <HelpText id={helpTextId} intent={invalid ? 'negative' : 'secondary'} showIcon={invalid}>
          {helpText}
        </HelpText>
      )}
    </div>
  );
});
